// src/api/axiosInstance.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    // The backend branches on this header: history-log search and the calendar
    // simulators return the admin-shaped payload only for "web".
    "x-client-type": "web",
  },
});

// Request interceptor: Always attach the current Access Token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── single-flight refresh ────────────────────────────────────────────────────
// A page that fires several requests at once gets several 401s at once. Without
// this, each one starts its own refresh: the first rotates the token and the
// rest then present a token the server has already replaced, so they fail and
// wipe localStorage. One refresh promise is shared by every waiter instead.
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    // ⚠️ The route is mounted at /api/users/goauthrefresh. This previously
    // omitted the /api prefix, so every refresh 404'd, fell into the catch and
    // cleared localStorage — the refresh path had never once succeeded.
    // Plain axios, not axiosInstance, so a 401 here cannot recurse.
    const res = await axios.post(
      `${API_BASE_URL}/api/users/goauthrefresh`,
      { refreshToken },
      { headers: { "x-client-type": "web" } }
    );

    const newToken = res.data?.token;
    if (!newToken) throw new Error("Refresh response carried no token");

    localStorage.setItem("token", newToken);
    return newToken;
  })().finally(() => {
    // Cleared whether it resolved or rejected, so a later 401 can try again.
    refreshPromise = null;
  });

  return refreshPromise;
}

// Response interceptor: Handle 401 by refreshing the token once per request.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true; // never loop

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // The refresh token is gone or also expired — this session is over.
        localStorage.clear();
        // window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
